import React, { useState, useEffect, useCallback } from "react";
import "./PartnerTransportListPage.css";
import { API_BASE_URL } from "../../config";

interface Transport {
  _id: string;
  TransportNumber?: string;
  StartDate?: string;
  Plant?: string;
  Sortimentbez?: string;
  FractionBez?: string;
  State?: string;
  CalWeek?: number;
  CustomerNumber?: string;
  SupplierName?: string;
  Quantity?: number;
  PlannedQuantity?: number;
  batchCount?: number; // Wird dynamisch geladen
  [key: string]: unknown;
}

interface BatchTransport {
  _id: string;
  transportBatchNumber: string;
  TransportNumber?: string;
  deliverydate?: string;
  truckLicensePlate?: string;
  driverName?: string;
  timeWindowFrom?: string;
  timeWindowTo?: string;
  provisioningNumber?: string;
  notes?: string;
  status?: number;
  quantityTrucks?: number;
  [key: string]: unknown;
}

interface PinLoginResponse {
  success: boolean;
  token: string;
  partnerId: string;
  lifnr: string;
  name: string;
  address?: string;
  city?: string;
}

const SESSION_STORAGE_KEY = "partner_auth_token";
const SESSION_STORAGE_INFO_KEY = "partner_info";

/**
 * Extrahiert TenantId aus URL (?tenant=xyz)
 */
function getTenantFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get("tenant");
}

const PartnerTransportListPage: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [tenant, setTenant] = useState("");
  const [pin, setPin] = useState("");
  const [partnerInfo, setPartnerInfo] = useState<{ lifnr: string; name: string; address?: string; city?: string } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [transports, setTransports] = useState<Transport[]>([]);
  const [isLoadingTransports, setIsLoadingTransports] = useState(false);

  // Master-Detail States
  const [selectedTransportId, setSelectedTransportId] = useState<string | null>(null);
  const [batchTransports, setBatchTransports] = useState<BatchTransport[]>([]);
  const [isLoadingBatches, setIsLoadingBatches] = useState(false);

  // Sortierung
  const [sortField, setSortField] = useState<string>("StartDate");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  // TenantId aus URL extrahieren beim Start
  useEffect(() => {
    const urlTenant = getTenantFromUrl();
    if (urlTenant) {
      setTenant(urlTenant);
    }
  }, []);

  // Session prüfen beim Start
  useEffect(() => {
    const token = sessionStorage.getItem(SESSION_STORAGE_KEY);
    const info = sessionStorage.getItem(SESSION_STORAGE_INFO_KEY);

    if (token && info) {
      try {
        const parsedInfo = JSON.parse(info);
        setPartnerInfo(parsedInfo);
        setIsAuthenticated(true);
      } catch {
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
        sessionStorage.removeItem(SESSION_STORAGE_INFO_KEY);
      }
    }
  }, []);

  const fetchTransports = useCallback(async () => {
    const token = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!token) {
      setIsAuthenticated(false);
      return;
    }

    setIsLoadingTransports(true);
    try {
      const params = new URLSearchParams();
      params.append("sort", `${sortField}:${sortDirection}`);
      params.append("limit", "100");

      // Tenant aus URL übernehmen (falls vorhanden)
      const tenant = getTenantFromUrl();
      if (tenant) {
        params.append("tenant", tenant);
      }

      const response = await fetch(`${API_BASE_URL}/api/partner/transports?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleLogout();
          throw new Error("Sitzung abgelaufen. Bitte melden Sie sich erneut an.");
        }
        throw new Error("Fehler beim Laden der Transporte");
      }

      const data: Transport[] = await response.json();
      
      // Chargen-Anzahl für jeden Transport laden
      const transportsWithBatchCount = await Promise.all(
        data.map(async (transport) => {
          if (!transport.TransportNumber) {
            return { ...transport, batchCount: 0 };
          }
          
          try {
            const batchResponse = await fetch(
              `${API_BASE_URL}/api/partner/batch-transports?TransportNumber=${transport.TransportNumber}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            if (batchResponse.ok) {
              const batches = await batchResponse.json();
              return { ...transport, batchCount: Array.isArray(batches) ? batches.length : 0 };
            }
          } catch (err) {
            console.error(`Fehler beim Laden der Chargen für ${transport.TransportNumber}:`, err);
          }
          
          return { ...transport, batchCount: 0 };
        })
      );
      
      setTransports(transportsWithBatchCount);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler beim Laden");
    } finally {
      setIsLoadingTransports(false);
    }
  }, [sortField, sortDirection]);

  // Transporte laden wenn authentifiziert oder Sortierung sich ändert
  useEffect(() => {
    if (isAuthenticated) {
      fetchTransports();
    }
  }, [isAuthenticated, sortField, sortDirection, fetchTransports]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!tenant || tenant.trim().length === 0) {
      setError("Bitte Mandant eingeben");
      return;
    }

    if (!pin || pin.trim().length === 0) {
      setError("Bitte PIN eingeben");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/partner/auth`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenant: tenant.trim(), pin: pin.trim() })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Authentifizierung fehlgeschlagen");
      }

      const data: PinLoginResponse = await response.json();

      if (data.success && data.token) {
        sessionStorage.setItem(SESSION_STORAGE_KEY, data.token);
        sessionStorage.setItem(
          SESSION_STORAGE_INFO_KEY,
          JSON.stringify({ 
            lifnr: data.lifnr, 
            name: data.name,
            address: data.address,
            city: data.city
          })
        );
        setPartnerInfo({ 
          lifnr: data.lifnr, 
          name: data.name,
          address: data.address,
          city: data.city
        });
        setIsAuthenticated(true);
        setPin("");
      } else {
        throw new Error("Ungültige Antwort vom Server");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler bei der Anmeldung");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    sessionStorage.removeItem(SESSION_STORAGE_INFO_KEY);
    setIsAuthenticated(false);
    setPartnerInfo(null);
    setTransports([]);
    setBatchTransports([]);
    setSelectedTransportId(null);
    setPin("");
  };

  // BatchTransports für ausgewählten Transport laden
  const fetchBatchTransports = useCallback(async (transportNumber: string) => {
    const token = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!token) {
      console.error("❌ Kein Token gefunden");
      return;
    }

    setIsLoadingBatches(true);
    console.log(`🔍 Lade BatchTransports für TransportNumber: ${transportNumber}`);
    
    try {
      const url = `${API_BASE_URL}/api/partner/batch-transports?TransportNumber=${transportNumber}`;
      console.log(`   URL: ${url}`);
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log(`   Response Status: ${response.status}`);
      console.log(`   Response OK: ${response.ok}`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Fehler beim Laden: ${response.status}`, errorText);
        throw new Error("Fehler beim Laden der Batch-Transporte");
      }

      const data: BatchTransport[] = await response.json();
      console.log(`✅ BatchTransports erhalten:`, data);
      console.log(`   Anzahl: ${Array.isArray(data) ? data.length : 'kein Array'}`);
      
      setBatchTransports(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("❌ Fehler beim Laden der Batch-Transporte:", err);
      setBatchTransports([]);
    } finally {
      setIsLoadingBatches(false);
    }
  }, []);

  // Handler für Zeilen-Klick
  const handleRowClick = (transport: Transport) => {
    if (selectedTransportId === transport._id) {
      // Gleiche Zeile nochmal geklickt -> schließen
      setSelectedTransportId(null);
      setBatchTransports([]);
    } else {
      // Neue Zeile ausgewählt
      setSelectedTransportId(transport._id);
      if (transport.TransportNumber) {
        fetchBatchTransports(transport.TransportNumber);
      }
    }
  };

  // Sortierung umschalten (analog zu YardListPage)
  const toggleSort = (field: string) => {
    if (sortField === field) {
      // Aktuelles Feld: Umschalten zwischen asc -> desc -> null
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else {
        // Zurück zum Default
        setSortField("StartDate");
        setSortDirection("desc");
      }
    } else {
      // Neues Feld: Beginne mit asc
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleRefresh = () => {
    fetchTransports();
  };

  // Login-Dialog
  if (!isAuthenticated) {
    // Prüfen ob tenant in URL vorhanden ist
    const urlTenant = getTenantFromUrl();
    
    if (!urlTenant) {
      return (
        <div className="partner-login-container">
          <div className="partner-login-card">
            <h1>Partner Login</h1>
            <p className="partner-login-subtitle">Bitte geben Sie Ihre Zugangsdaten ein</p>
            <div className="partner-error" style={{ marginBottom: "1rem" }}>
              Fehler: Kein Mandant in der URL angegeben. Bitte verwenden Sie einen gültigen Link.
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="partner-login-container">
        <div className="partner-login-card">
          <h1>Partner Login</h1>
          <p className="partner-login-subtitle">Bitte geben Sie Ihre PIN ein</p>
          
          <form className="partner-login-form" onSubmit={handleLogin}>
            <div className="partner-form-group">
              <label htmlFor="pin">PIN</label>
              <input
                id="pin"
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                disabled={isLoading}
                autoFocus
                placeholder="Ihre 6-stellige PIN"
              />
            </div>

            {error && <div className="partner-error">{error}</div>}

            <button type="submit" className="partner-login-btn" disabled={isLoading}>
              {isLoading ? "Wird geprüft..." : "Anmelden"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Transporte-Liste
  return (
    <div className="partner-transport-page">
      <header className="partner-header">
        <div className="partner-header-content">
          <h1>Meine Transporte</h1>
          {partnerInfo && (
            <div className="partner-info">
              <div className="partner-info-row">
                <span className="partner-name">{partnerInfo.name}</span>
                <span className="partner-lifnr">Spediteurnr: {partnerInfo.lifnr}</span>
              </div>
              {(partnerInfo.address || partnerInfo.city) && (
                <div className="partner-info-row partner-address">
                  {partnerInfo.address && <span>{partnerInfo.address}</span>}
                  {partnerInfo.city && <span>{partnerInfo.city}</span>}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="partner-header-actions">
          <button onClick={handleRefresh} className="partner-refresh-btn" disabled={isLoadingTransports}>
            Aktualisieren
          </button>
          <button onClick={handleLogout} className="partner-logout-btn">
            Abmelden
          </button>
        </div>
      </header>

      {isLoadingTransports ? (
        <div className="partner-loading">Lade Transporte...</div>
      ) : error ? (
        <div className="partner-error">{error}</div>
      ) : (
        <div className="partner-master-detail-container">
          <div className="partner-table-wrapper">
            <table className="partner-table">
              <thead>
                <tr>
                  <th>Status</th>
                <th>
                  <button
                    type="button"
                    className={`partner-sort-button ${sortField === "TransportNumber" ? `partner-sort-button--${sortDirection}` : ""}`}
                    onClick={() => toggleSort("TransportNumber")}
                  >
                    <span className="partner-sort-label">Transportnr.</span>
                    <span className="partner-sort-icon">
                      {sortField === "TransportNumber" ? (sortDirection === "asc" ? "▲" : "▼") : "⇅"}
                    </span>
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    className={`partner-sort-button ${sortField === "Sortimentbez" ? `partner-sort-button--${sortDirection}` : ""}`}
                    onClick={() => toggleSort("Sortimentbez")}
                  >
                    <span className="partner-sort-label">Sortiment</span>
                    <span className="partner-sort-icon">
                      {sortField === "Sortimentbez" ? (sortDirection === "asc" ? "▲" : "▼") : "⇅"}
                    </span>
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    className={`partner-sort-button ${sortField === "FractionBez" ? `partner-sort-button--${sortDirection}` : ""}`}
                    onClick={() => toggleSort("FractionBez")}
                  >
                    <span className="partner-sort-label">Fraktion</span>
                    <span className="partner-sort-icon">
                      {sortField === "FractionBez" ? (sortDirection === "asc" ? "▲" : "▼") : "⇅"}
                    </span>
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    className={`partner-sort-button ${sortField === "SupplierName" ? `partner-sort-button--${sortDirection}` : ""}`}
                    onClick={() => toggleSort("SupplierName")}
                  >
                    <span className="partner-sort-label">Lieferant</span>
                    <span className="partner-sort-icon">
                      {sortField === "SupplierName" ? (sortDirection === "asc" ? "▲" : "▼") : "⇅"}
                    </span>
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    className={`partner-sort-button ${sortField === "Quantity" ? `partner-sort-button--${sortDirection}` : ""}`}
                    onClick={() => toggleSort("Quantity")}
                  >
                    <span className="partner-sort-label">Gesamt LKWs</span>
                    <span className="partner-sort-icon">
                      {sortField === "Quantity" ? (sortDirection === "asc" ? "▲" : "▼") : "⇅"}
                    </span>
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    className={`partner-sort-button ${sortField === "PlannedQuantity" ? `partner-sort-button--${sortDirection}` : ""}`}
                    onClick={() => toggleSort("PlannedQuantity")}
                  >
                    <span className="partner-sort-label">Geplant</span>
                    <span className="partner-sort-icon">
                      {sortField === "PlannedQuantity" ? (sortDirection === "asc" ? "▲" : "▼") : "⇅"}
                    </span>
                  </button>
                </th>
                <th>Ungeplant</th>
                <th>
                  <button
                    type="button"
                    className={`partner-sort-button ${sortField === "batchCount" ? `partner-sort-button--${sortDirection}` : ""}`}
                    onClick={() => toggleSort("batchCount")}
                  >
                    <span className="partner-sort-label">Chargen</span>
                    <span className="partner-sort-icon">
                      {sortField === "batchCount" ? (sortDirection === "asc" ? "▲" : "▼") : "⇅"}
                    </span>
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    className={`partner-sort-button ${sortField === "CalWeek" ? `partner-sort-button--${sortDirection}` : ""}`}
                    onClick={() => toggleSort("CalWeek")}
                  >
                    <span className="partner-sort-label">KW</span>
                    <span className="partner-sort-icon">
                      {sortField === "CalWeek" ? (sortDirection === "asc" ? "▲" : "▼") : "⇅"}
                    </span>
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {transports.length === 0 ? (
                <tr>
                  <td colSpan={10} className="partner-no-data">
                    Keine Transporte gefunden
                  </td>
                </tr>
              ) : (
                transports.map((transport) => {
                  const quantity = transport.Quantity ?? 0;
                  const planned = transport.PlannedQuantity ?? 0;
                  const unplanned = quantity - planned;

                  // Status-Farbe: Rot (ungeplant) → Gelb (teilweise) → Grün (komplett)
                  let statusColor = "#dc2626"; // Rot
                  if (planned > 0 && planned < quantity) {
                    statusColor = "#f59e0b"; // Gelb
                  } else if (planned >= quantity && quantity > 0) {
                    statusColor = "#16a34a"; // Grün
                  }

                  return (
                    <tr 
                      key={transport._id}
                      onClick={() => handleRowClick(transport)}
                      className={selectedTransportId === transport._id ? "partner-row-selected" : ""}
                      style={{ cursor: "pointer" }}
                    >
                      <td style={{ textAlign: "center" }}>
                        {/* LKW-Icon mit Status-Farbe */}
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          style={{ display: "inline-block", verticalAlign: "middle" }}
                        >
                          <path
                            d="M17 8h2l3 4v5h-2a3 3 0 1 1-6 0H9a3 3 0 1 1-6 0H1V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v2z"
                            fill={statusColor}
                            stroke={statusColor}
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </td>
                      <td>{transport.TransportNumber || "-"}</td>
                      <td>{transport.Sortimentbez || "-"}</td>
                      <td>{transport.FractionBez || "-"}</td>
                      <td>{transport.SupplierName || "-"}</td>
                      <td style={{ textAlign: "center" }}>{quantity}</td>
                      <td style={{ textAlign: "center" }}>{planned}</td>
                      <td
                        style={{
                          textAlign: "center",
                          fontWeight: unplanned > 0 ? "bold" : "normal",
                          color: unplanned > 0 ? "#dc2626" : "inherit",
                        }}
                      >
                        {unplanned}
                      </td>
                      <td style={{ textAlign: "center", fontWeight: "bold" }}>
                        {transport.batchCount ?? "-"}
                      </td>
                      <td style={{ textAlign: "center" }}>{transport.CalWeek || "-"}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          {transports.length > 0 && (
            <div className="partner-summary">Gesamt: {transports.length} Transporte</div>
          )}
          </div>

          {/* Detail Panel rechts */}
          {selectedTransportId && (
            <div className="partner-detail-panel">
              <div className="partner-detail-header">
                <h3>Batch-Transporte</h3>
                <button 
                  className="partner-detail-close"
                  onClick={() => {
                    setSelectedTransportId(null);
                    setBatchTransports([]);
                  }}
                >
                  ✕
                </button>
              </div>

              {isLoadingBatches ? (
                <div className="partner-detail-loading">Lade Details...</div>
              ) : batchTransports.length === 0 ? (
                <div className="partner-detail-empty">Keine Batch-Transporte gefunden</div>
              ) : (
                <div className="partner-detail-content">
                  <table className="partner-detail-table">
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th>Chargen-Nr.</th>
                        <th>LKW's</th>
                        <th>Datum</th>
                        <th>Zeitfenster</th>
                        <th>Bereitst.-Nr.</th>
                        <th>KFZ-Kennzeichen</th>
                        <th>Fahrer</th>
                        <th>Notizen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {batchTransports.map((batch) => {
                        // Status-Icon basierend auf batch.status
                        let batchStatusColor = "#94a3b8"; // Grau (kein Status)
                        if (batch.status === 1) batchStatusColor = "#dc2626"; // Rot (Geplant)
                        else if (batch.status === 2) batchStatusColor = "#f59e0b"; // Gelb (In Bearbeitung)
                        else if (batch.status === 3) batchStatusColor = "#16a34a"; // Grün (Abgeschlossen)

                        // Zeitfenster formatieren
                        const formatTime = (dateString?: string) => {
                          if (!dateString) return "-";
                          try {
                            const date = new Date(dateString);
                            return date.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
                          } catch {
                            return "-";
                          }
                        };

                        const timeWindow = batch.timeWindowFrom && batch.timeWindowTo
                          ? `${formatTime(batch.timeWindowFrom)} - ${formatTime(batch.timeWindowTo)}`
                          : "-";

                        // Datum formatieren
                        const formatDate = (dateString?: string) => {
                          if (!dateString) return "-";
                          try {
                            const date = new Date(dateString);
                            return date.toLocaleDateString("de-DE");
                          } catch {
                            return "-";
                          }
                        };

                        return (
                          <tr key={batch._id}>
                            <td style={{ textAlign: "center" }}>
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill={batchStatusColor}
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <circle cx="8" cy="8" r="6" />
                              </svg>
                            </td>
                            <td>{batch.transportBatchNumber || "-"}</td>
                            <td style={{ textAlign: "center" }}>{batch.quantityTrucks ?? "-"}</td>
                            <td>{formatDate(batch.deliverydate)}</td>
                            <td>{timeWindow}</td>
                            <td>{batch.provisioningNumber || "-"}</td>
                            <td>{batch.truckLicensePlate || "-"}</td>
                            <td>{batch.driverName || "-"}</td>
                            <td style={{ fontSize: "0.85rem", color: "#64748b" }}>
                              {batch.notes || "-"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PartnerTransportListPage;
